/**
 * @file Reads the signed-in user's home summaries, one
 * `users/{uid}/analyticsSummaries/{level}` document per scope level, written by
 * the analytics service after every ingestion cycle. Consumer:
 * `useHomeGoalProgress`. Each level reads as not visible, loading, not found,
 * failed or found, so a level with no document is never mistaken for one still
 * loading. Only the levels the rules let this user read get a store: any
 * metrics right for `self` and `team`, `metricsOrgRead` or `metricsAdmin` for
 * `org`; `orgsAdmin` does not count. A change of user or rights rebuilds every
 * store, and one proxy nests the live stores' states for a single snapshot.
 */

import { useContext, useEffect, useMemo } from 'react';
import { proxy, useSnapshot } from 'valtio';

import {
  ANALYTICS_SCOPE_LEVELS,
  analyticsSummaryPath,
  type AnalyticsScopeLevel,
  type AnalyticsSummary,
} from '#types';
import {
  analyticsByLevel,
  analyticsRights,
  type AnalyticsSummaryRead,
} from '@/analytics';
import { UserContext } from '../../providers/UserContext';
import { createDocumentStore } from '../../store';

export const useHomeAnalyticsSummary = (): Readonly<
  Record<AnalyticsScopeLevel, AnalyticsSummaryRead>
> => {
  const { user, userRights } = useContext(UserContext);
  const uid = user?.uid;
  const { canRead, canReadOrg } = analyticsRights(userRights);
  const readsOrg = Boolean(uid) && canReadOrg;
  const readsOwn = Boolean(uid) && canRead;

  const stores = useMemo(
    () =>
      analyticsByLevel((level) =>
        uid && (level === 'org' ? readsOrg : readsOwn)
          ? createDocumentStore<AnalyticsSummary>(
              analyticsSummaryPath(uid, level),
            )
          : null,
      ),
    [uid, readsOwn, readsOrg],
  );

  useEffect(() => {
    const connected = ANALYTICS_SCOPE_LEVELS.flatMap(
      (level) => stores[level] ?? [],
    );
    for (const store of connected) void store.connect();
    return () => {
      for (const store of connected) store.disconnect();
    };
  }, [stores]);

  const states = useMemo(
    () => proxy(analyticsByLevel((level) => stores[level]?.state ?? null)),
    [stores],
  );
  const snapshot = useSnapshot(states);

  return analyticsByLevel((level): AnalyticsSummaryRead => {
    const state = snapshot[level];
    if (state === null) return { status: 'notVisible' };
    if (state.data) return { status: 'found', summary: state.data };
    if (state.error) return { status: 'failed', message: state.error.message };
    if (state.loading) return { status: 'loading' };
    return { status: 'notFound' };
  });
};
